import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Show, useDataProvider, useVersion } from 'react-admin';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Chip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';

import useDocumentTitle from '../../hooks/useDocumentTitle';
import {
  normalizeSilaeId,
  ORIGINE_LABELS,
  ORIGINE_VIVIER_CONSTRUIT,
  ORIGINE_NON_CONFORME,
} from '../../helpers';
import VivierPanel from './components/VivierPanel';
import HireDialog from './components/HireDialog';
import DeleteFicheDialog from './components/DeleteFicheDialog';
import EditIdentityDialog from './components/EditIdentityDialog';

// Material-UI Chip colour for an `origine` code.
const origineColor = (origine) => {
  if (origine === ORIGINE_VIVIER_CONSTRUIT) {
    return 'primary';
  }
  if (origine === ORIGINE_NON_CONFORME) {
    return 'secondary';
  }
  return 'default';
};

// care returns `birthdate` as a plain YYYY-MM-DD (Postgres date column, same
// format the <DateInput> in EditIdentityDialog reads/writes) — display it as
// DD/MM/YYYY, the format already used across the repo for a date (moment +
// `.format('DD/MM/YYYY')`, e.g. Cleaner/detail/getTrialRenewalDate.js,
// components/field/ContractField.js). Strict parsing so a missing/malformed
// value returns null rather than "Invalid date" — Field's own null check
// then falls back to "—", the same as for any other absent field.
const formatBirthdate = (value) => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : null;
};

const useStyles = makeStyles((theme) => {
  return {
    label: {
      color: theme.palette.text.secondary,
    },
    field: {
      marginBottom: theme.spacing(0.5),
    },
    sectionLabel: {
      color: theme.palette.text.disabled,
      fontWeight: 600,
      marginTop: theme.spacing(1),
    },
  };
});

const Field = ({ label, value, classes }) => {
  return (
    <Grid item xs={6} sm={3} className={classes.field}>
      <Typography variant="caption" className={classes.label}>
        {label}
      </Typography>
      <Typography variant="body2">
        {value === null || value === undefined || value === ''
          ? '—'
          : String(value)}
      </Typography>
    </Grid>
  );
};

Field.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
};

Field.defaultProps = {
  value: null,
};

// Full-width group heading within the same Grid (Identité / Contacts /
// Naissance / Adresse) — a plain Grid item forces a new row without needing
// to split the identity card into several Grids.
const SectionLabel = ({ label, classes }) => {
  return (
    <Grid item xs={12}>
      <Typography variant="overline" className={classes.sectionLabel}>
        {label}
      </Typography>
    </Grid>
  );
};

SectionLabel.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
};

// Identity-only header for a vivier record (no Paie/Timecard/Affectations/
// Contrat, per plan 013 B4).
const IdentityCard = ({ record, classes, action, loadWarning }) => {
  if (!record) {
    return null;
  }
  const origineLabel = record.origine
    ? ORIGINE_LABELS[record.origine] || record.origine
    : null;
  return (
    <Grid item xs={12}>
      <Card>
        <CardHeader
          title={record.fullname || 'Vivier'}
          subheader="Identité"
          action={
            <Grid container spacing={1} alignItems="center" wrap="nowrap">
              {origineLabel ? (
                <Grid item>
                  <Chip
                    size="small"
                    label={origineLabel}
                    color={origineColor(record.origine)}
                  />
                </Grid>
              ) : null}
              {action ? <Grid item>{action}</Grid> : null}
            </Grid>
          }
        />
        <CardContent>
          <Grid container spacing={1}>
            <SectionLabel label="Identité" classes={classes} />
            <Field
              label="Civilité"
              value={record.salutation}
              classes={classes}
            />
            <Field label="Prénom" value={record.firstname} classes={classes} />
            <Field label="Nom" value={record.lastname} classes={classes} />
            {/* Id Silae / Statut: always from care.person_vivier (the view),
                never from the richer cleaner record merged in by the caller
                — read-only, see VivierShowView's displayRecord. */}
            <Field label="Id Silae" value={record.silae_id} classes={classes} />
            <Field
              label="Statut"
              value={record.employee_status}
              classes={classes}
            />

            <SectionLabel label="Contacts" classes={classes} />
            <Field label="E-mail" value={record.email} classes={classes} />
            <Field
              label="Mobile"
              value={record.mobile_phone}
              classes={classes}
            />
            <Field label="Téléphone" value={record.phone} classes={classes} />

            <SectionLabel label="Naissance" classes={classes} />
            <Field
              label="Naissance"
              value={formatBirthdate(record.birthdate)}
              classes={classes}
            />
            <Field
              label="Lieu de naissance"
              value={record.birthplace}
              classes={classes}
            />
            <Field
              label="Nationalité"
              value={record.nationality}
              classes={classes}
            />

            <SectionLabel label="Adresse" classes={classes} />
            <Field
              label="Adresse"
              value={record.mailing_street}
              classes={classes}
            />
            <Field
              label="Complément d'adresse"
              value={record.mailing_additional}
              classes={classes}
            />
            <Field
              label="Code postal"
              value={record.mailing_postal_code}
              classes={classes}
            />
            <Field
              label="Ville"
              value={record.mailing_city}
              classes={classes}
            />
          </Grid>
          {loadWarning && (
            <Typography
              variant="caption"
              color="error"
              style={{ display: 'block', marginTop: 8 }}
            >
              Certaines informations n&apos;ont pas pu être chargées.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

IdentityCard.propTypes = {
  record: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  action: PropTypes.node,
  loadWarning: PropTypes.bool,
};

IdentityCard.defaultProps = {
  record: undefined,
  action: null,
  loadWarning: false,
};

// `record` is injected by <Show> into its direct child.
export const VivierShowView = ({ record }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  // Bumped by refresh() (react-admin's global "force refetch" signal, see
  // ra-core's useVersion) — must be a dependency of the fetch below, or
  // EditIdentityDialog's post-save refresh() would leave this card showing
  // the pre-edit values forever (the actual bug this fetch exists to fix
  // would then only look fixed).
  const version = useVersion();
  const [editOpen, setEditOpen] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const silaeId = normalizeSilaeId(record && record.silae_id);
  const sourceCleanerId = record && record.id ? String(record.id) : '';
  const cleanerId = record && record.id;

  // care.person_vivier (the view <Show> loads `record` from) exposes only 15
  // columns — notably NOT phone/birthdate/nationality/salutation/birthplace/
  // mailing_street/mailing_additional/mailing_postal_code, all of which
  // EditIdentityDialog can write. Without this, the identity card keeps
  // showing "—" for a field no matter what gets saved: not a display bug in
  // the strict sense, a wrong data source. Fetch the full `cleaner` row (same
  // resource EditIdentityDialog already writes through) and merge it in for
  // display — independently of that dialog's own pre-fill fetch, which is
  // separate and untouched.
  const [fullCleanerRecord, setFullCleanerRecord] = useState(null);
  const [fullCleanerError, setFullCleanerError] = useState(false);

  useEffect(() => {
    if (!cleanerId) {
      setFullCleanerRecord(null);
      setFullCleanerError(false);
      return undefined;
    }
    let cancelled = false;
    dataProvider
      .getOne('cleaner', { id: cleanerId })
      .then(({ data }) => {
        if (!cancelled) {
          setFullCleanerError(false);
          setFullCleanerRecord(data);
        }
      })
      .catch(() => {
        // Degrade to whatever care.person_vivier already gave us (below)
        // rather than blocking the page or clearing what's already shown —
        // this fetch is a display enhancement, not a hard requirement.
        if (!cancelled) {
          setFullCleanerError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cleanerId, dataProvider, version]);

  // No loading state gates this: while the fetch is in flight (or if it
  // fails), we simply keep showing person_vivier's own fields as-is (blank
  // for the ones it doesn't carry, same as before this fix) instead of
  // flashing the whole card to a spinner/blank state once the richer data
  // resolves, the missing fields just fill in.
  const displayRecord = useMemo(() => {
    if (!record || !fullCleanerRecord) {
      return record;
    }
    return {
      ...record,
      ...fullCleanerRecord,
      // These are view-level concepts (read-only, or simply not part of the
      // `cleaner` row) — always keep person_vivier's own values, never let
      // the cleaner fetch shadow them.
      id: record.id,
      fullname: record.fullname,
      silae_id: record.silae_id,
      employee_status: record.employee_status,
      origine: record.origine,
      has_contract: record.has_contract,
      has_affectation: record.has_affectation,
    };
  }, [record, fullCleanerRecord]);

  // Modifier / Embaucher / Supprimer are gated on the loaded record only.
  // Authorization is enforced server-side (a 403 surfaces from the care
  // backend), so we deliberately do NOT add a client-side role gate here: the
  // login user object exposed by the authProvider does not reliably carry a
  // `function` / `is_admin` field, which made the previous isHrOrManager()
  // check fail closed and hid the action for everyone.
  const canHire = !!(record && record.id);
  // Same gate as canHire (any loaded fiche): editing identity has no
  // contract/affectation precondition, unlike canDelete below — kept as its
  // own constant rather than reusing canHire so the two can diverge later
  // without silently coupling.
  const canEdit = !!(record && record.id);
  // `has_contract` / `has_affectation` come from care.person_vivier (plan 034
  // lot G, care_v1.2 migration). Fail closed with a strict `=== false`, not
  // `!record.has_contract`: until that view migration ships in production,
  // both columns are `undefined`, and `!undefined` evaluates to `true` — that
  // would show the button for everyone, the exact opposite of the intent
  // (929/957 vivier profiles have a contract the server already refuses to
  // delete). A future "simplification" to `!record.has_contract` would
  // silently reintroduce that bug. Only show the button once both absences
  // are proven, not merely un-truthy.
  const canDelete =
    !!record?.silae_id &&
    record.has_contract === false &&
    record.has_affectation === false;

  const headerActions = (
    <Grid container spacing={1} alignItems="center" wrap="nowrap">
      {canEdit ? (
        <Grid item>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              return setEditOpen(true);
            }}
          >
            Modifier
          </Button>
        </Grid>
      ) : null}
      {canDelete ? (
        <Grid item>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            style={{ color: '#f44336', borderColor: '#f44336' }}
            onClick={() => {
              return setDeleteOpen(true);
            }}
          >
            Supprimer la fiche
          </Button>
        </Grid>
      ) : null}
      {canHire ? (
        <Grid item>
          <Button
            size="small"
            color="primary"
            variant="contained"
            startIcon={<HowToRegIcon />}
            onClick={() => {
              return setHireOpen(true);
            }}
          >
            Embaucher
          </Button>
        </Grid>
      ) : null}
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <IdentityCard
        record={displayRecord}
        classes={classes}
        action={headerActions}
        loadWarning={fullCleanerError}
      />
      <Grid item xs={12}>
        <VivierPanel
          silaeId={silaeId}
          sourceCleanerId={sourceCleanerId}
          showDocuments
          showAvailability
        />
      </Grid>
      {canHire && (
        <HireDialog
          open={hireOpen}
          onClose={() => {
            return setHireOpen(false);
          }}
          record={record}
        />
      )}
      {canDelete && (
        <DeleteFicheDialog
          open={deleteOpen}
          onClose={() => {
            return setDeleteOpen(false);
          }}
          record={record}
        />
      )}
      {canEdit && (
        <EditIdentityDialog
          open={editOpen}
          onClose={() => {
            return setEditOpen(false);
          }}
          record={record}
        />
      )}
    </Grid>
  );
};

VivierShowView.propTypes = {
  record: PropTypes.object,
};

VivierShowView.defaultProps = {
  record: undefined,
};

// Page title = person's fullname (never the raw record id). `record` is injected
// by <Show> into its `title` element.
const VivierShowTitle = ({ record }) => {
  if (!record) {
    return <span>Vivier</span>;
  }
  const composed = [record.firstname, record.lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  return <span>{record.fullname || composed || record.id}</span>;
};

VivierShowTitle.propTypes = {
  record: PropTypes.object,
};

VivierShowTitle.defaultProps = {
  record: undefined,
};

const VivierShow = (props) => {
  useDocumentTitle('Vivier');
  return (
    <Show
      {...props}
      component="div"
      actions={false}
      title={<VivierShowTitle />}
    >
      <VivierShowView />
    </Show>
  );
};

export default VivierShow;
